import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, Search, Monitor, LogIn, LogOut, Users } from "lucide-react";
import "./ApplicationLogs.css";
import {API_BASE_URL} from "../../../../api"; // Reuse styling

const formatDateTime = (value) => {
    if (!value) return "—";
    try {
        const date = new Date(value);
        return date.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
};

const SessionLogs = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const fetchSessions = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${API_BASE_URL}/api/admin/config/sessions`, {
                headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
            });
            setSessions(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error("Erreur chargement sessions:", err);
            setError("Impossible de charger les sessions.");
            setSessions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    const filteredSessions = useMemo(() => {
        const term = search.trim().toLowerCase();
        return sessions.filter((s) => {
            const userName = `${s.user?.firstName || ""} ${s.user?.lastName || ""}`.toLowerCase();
            const userEmail = (s.user?.email || "").toLowerCase();
            const ip = (s.ipAddress || "").toLowerCase();

            const matchesSearch =
                !term ||
                userName.includes(term) ||
                userEmail.includes(term) ||
                ip.includes(term);

            const isOnline = !s.logoutAt;
            const matchesStatus =
                statusFilter === "ALL" ||
                (statusFilter === "ACTIVE" && isOnline) ||
                (statusFilter === "FINISHED" && !isOnline);

            return matchesSearch && matchesStatus;
        });
    }, [sessions, search, statusFilter]);

    return (
        <div className="application-logs-page">
            <div className="application-logs-header">
                <div>
                    <h3>Historique des sessions utilisateurs</h3>
                </div>
                <div className="application-logs-header-actions">
                    <button className="al-btn al-btn-primary" onClick={fetchSessions} disabled={loading}>
                        <RefreshCw size={14} className={loading ? "al-spin" : ""} />
                        {loading ? "Chargement..." : "Actualiser"}
                    </button>
                </div>
            </div>

            <div className="application-logs-toolbar">
                <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
                    <Search size={14} style={{ position: "absolute", left: 12, color: "#6e91c1" }} />
                    <input
                        type="text"
                        className="application-logs-search"
                        style={{ paddingLeft: 34 }}
                        placeholder="Rechercher par utilisateur, email, IP..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <select
                    className="application-logs-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="ALL">Tous les statuts</option>
                    <option value="ACTIVE">Actif (En cours)</option>
                    <option value="FINISHED">Terminé</option>
                </select>

                <div className="application-logs-count">
                    {filteredSessions.length} résultat{filteredSessions.length > 1 ? "s" : ""}
                </div>
            </div>

            {error && <div className="application-logs-alert error">{error}</div>}

            <div className="application-logs-table-wrapper">
                {loading ? (
                    <div className="al-loading">
                        <div className="al-spinner" />
                        <span>Chargement des sessions...</span>
                    </div>
                ) : filteredSessions.length === 0 ? (
                    <div className="al-empty">Aucune session trouvée</div>
                ) : (
                    <table className="application-logs-table">
                        <thead>
                        <tr>
                            <th>Utilisateur</th>
                            <th>Début</th>
                            <th>Fin</th>
                            <th>Durée</th>
                            <th>IP / Appareil</th>
                            <th>Statut</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredSessions.map((s) => {
                            const duration = s.logoutAt
                                ? Math.round((new Date(s.logoutAt) - new Date(s.loginAt)) / 60000)
                                : null;

                            return (
                                <tr key={s.id}>
                                    <td className="al-name">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e8edf7', color: '#2a5ca9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                                                {s.user?.firstName?.[0]}{s.user?.lastName?.[0]}
                                            </div>
                                            <div>
                                                <div>{s.user?.firstName} {s.user?.lastName}</div>
                                                <div className="al-muted" style={{ fontSize: 10 }}>{s.user?.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="al-date">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <LogIn size={12} color="#10b981" />
                                            {formatDateTime(s.loginAt)}
                                        </div>
                                    </td>
                                    <td className="al-date">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <LogOut size={12} color={s.logoutAt ? "#ef4444" : "#9ca3af"} />
                                            {s.logoutAt ? formatDateTime(s.logoutAt) : "En cours"}
                                        </div>
                                    </td>
                                    <td className="al-name">
                                        {duration !== null ? `${duration} min` : "—"}
                                    </td>
                                    <td className="application-logs-useragent">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Monitor size={12} /> {s.ipAddress || "127.0.0.1"}
                                        </div>
                                        <div className="al-muted" style={{ fontSize: 9 }}>{s.userAgent}</div>
                                    </td>
                                    <td>
                                        {!s.logoutAt ? (
                                            <span className="application-logs-status" style={{ background: '#dcfce7', color: '#166534' }}>ACTIF</span>
                                        ) : (
                                            <span className="application-logs-status" style={{ background: '#f1f5f9', color: '#475569' }}>TERMINÉ</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SessionLogs;
